import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField } from '@mui/material';
import Button from '../../components/button';
import { LessonService } from '../../services/lessons/lesson.service';
import { LessonPreviewUI, LessonStep } from '../../components/lesson-preview';
import { showErrorToast } from '../../components/Toaster';
import './shared-lesson.scss';

const SharedLesson: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const lessonService = new LessonService();

  const [pin, setPin] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lessonData, setLessonData] = useState<{ topic: any; steps: LessonStep[] } | null>(null);

  const handleVerifyPin = async () => {
    if (!pin || pin.length !== 4) {
      showErrorToast(t('invalidPin'));
      return;
    }

    setLoading(true);
    try {
      const response = await lessonService.getSharedLesson(token as string, pin);
      setLessonData({
        topic: { name: response.topic_name, description: '' },
        steps: response.content || []
      });
      setIsAuthenticated(true);
    } catch (e: any) {
      if (e.response?.status === 401 || e.response?.status === 403 || e.response?.status === 404) {
        showErrorToast(t('incorrectPin'));
      } else {
        showErrorToast(t('errorLoadingLesson'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="shared-lesson-container" dir="rtl">
      <Box className="content-wrapper">
        {!isAuthenticated ? (
          <Box className="pin-auth-box">
            <Typography variant="h5" className="title" gutterBottom>
              {t('sharedLessonAccess')}
            </Typography>
            <Typography variant="body1" color="textSecondary" className="description">
              {t('enterPinToAccess')}
            </Typography>
            <TextField
              autoFocus
              type="password"
              variant="outlined"
              placeholder="PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              inputProps={{ maxLength: 4 }}
              fullWidth
              className="pin-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleVerifyPin();
              }}
            />
            <Button
              text={t('verifyPin')}
              color="blue"
              onClick={handleVerifyPin}
              loader={loading}
              className="verify-btn"
            />
          </Box>
        ) : (
          <Box className="lesson-preview-box">
            <Box className="lesson-header">
              <Typography variant="h4" className="lesson-title">
                {lessonData?.topic?.name}
              </Typography>
              {lessonData?.topic?.description && (
                <Typography variant="subtitle1" color="textSecondary" className="lesson-description">
                  {lessonData.topic.description}
                </Typography>
              )}
            </Box>
            <LessonPreviewUI steps={lessonData?.steps || []} topicName={lessonData?.topic?.name} />
          </Box>
        )}
      </Box>
    </div>
  );
};

export default SharedLesson;
